import { z } from 'zod'
import type { CalculatorDefinition } from '@/types/calculator'
import { positiveMoney } from '@/calculators/shared/schema'
import { calculateMarkupProfit } from './calculation'
import { content, faqs } from './content'
import type { MarkupProfitInputs, MarkupProfitResult } from './types'

const schema = z.object({
  costPerUnit: positiveMoney('Enter a cost per unit').refine((v) => v > 0, {
    message: 'Cost must be greater than 0',
  }),
  sellingPricePerUnit: positiveMoney('Enter a selling price').refine((v) => v >= 0, {
    message: 'Selling price must be at least 0',
  }),
  quantity: z.number().int().min(1, { message: 'Quantity must be at least 1' }),
  overhead: positiveMoney(),
})

const defaults: MarkupProfitInputs = {
  costPerUnit: 50,
  sellingPricePerUnit: 75,
  quantity: 100,
  overhead: 500,
}

export const markupProfitDefinition: CalculatorDefinition<MarkupProfitInputs, MarkupProfitResult> =
  {
    id: 'markup-profit',
    slug: 'markup-profit-calculator',
    category: 'business',
    title: 'Markup, Margin & Profit Calculator',
    shortTitle: 'Markup & Profit',
    description:
      'Calculate markup, margin, gross profit and break-even for South African small businesses.',
    inputs: [
      {
        name: 'costPerUnit',
        label: 'Cost per unit',
        type: 'money',
        required: true,
        unit: 'R',
        placeholder: 'e.g. 50',
      },
      {
        name: 'sellingPricePerUnit',
        label: 'Selling price per unit',
        type: 'money',
        required: true,
        unit: 'R',
        placeholder: 'e.g. 75',
      },
      {
        name: 'quantity',
        label: 'Quantity sold',
        type: 'number',
        required: true,
        min: 1,
        step: 1,
      },
      {
        name: 'overhead',
        label: 'Overhead / fixed costs',
        type: 'money',
        required: false,
        unit: 'R',
        placeholder: '0',
      },
    ],
    defaults,
    schema,
    calculate: calculateMarkupProfit,
    resultSections: [
      {
        id: 'grossProfit',
        label: 'Gross profit',
        format: 'currency',
        value: (r: MarkupProfitResult) => r.grossProfit,
        highlighted: true,
      },
      {
        id: 'netProfit',
        label: 'Net profit',
        format: 'currency',
        value: (r: MarkupProfitResult) => r.netProfit,
        highlighted: true,
      },
      {
        id: 'revenue',
        label: 'Revenue',
        format: 'currency',
        value: (r: MarkupProfitResult) => r.revenue,
      },
      {
        id: 'totalCost',
        label: 'Total cost',
        format: 'currency',
        value: (r: MarkupProfitResult) => r.totalCost,
      },
      {
        id: 'markup',
        label: 'Markup',
        format: 'percent',
        value: (r: MarkupProfitResult) => r.markup,
      },
      {
        id: 'margin',
        label: 'Margin',
        format: 'percent',
        value: (r: MarkupProfitResult) => r.margin,
      },
      {
        id: 'breakEvenUnits',
        label: 'Break-even units',
        format: 'number',
        value: (r: MarkupProfitResult) => r.breakEvenUnits,
      },
      {
        id: 'recommendedPriceForMargin',
        label: 'Price for 30% margin',
        format: 'currency',
        value: (r: MarkupProfitResult) => r.recommendedPriceForMargin,
      },
    ],
    assumptions: [
      {
        name: 'Business accounting definitions',
        url: 'https://www.sars.gov.za/',
        reviewedAt: '2026-09-11',
        effectiveFrom: '2026-09-01',
        notes:
          'Markup = (selling price - cost) / cost. Margin = (revenue - variable costs) / revenue. Excludes VAT unless you enter VAT-inclusive figures.',
      },
    ],
    related: ['vat-calculator', 'salary-calculator', 'bond-calculator'],
    seo: {
      title: 'Markup & Profit Margin Calculator South Africa',
      description:
        'Calculate markup, profit margin, gross and net profit and break-even units with this markup calculator for small businesses.',
      keywords: ['markup calculator', 'profit margin', 'gross profit', 'South Africa business'],
    },
    content,
    faqs,
  }
